import { useState, useEffect, useRef, useMemo } from 'react'
import { Asset, LinkWithData } from '../../types'
import { validations } from '../../validations'

type ContentWithLinkPreviewProps = {
  content: string
  linksWithData: LinkWithData[]
  enabled?: boolean
}

/**
 * useContentWithLinkPreviewAssetCarousel is a custom hook to use in content
 * types that are connected to the LinkPreviewAssetCarousel component. It
 * takes the main body of content and a post's linksWithData prop.
 *
 * It works by registering when content has been updated and by checking after
 * 2 seconds of not typing whether or not a url has been entered. This url
 * will be used to create a new linksWithData post option entry.
 *
 * If a link preview already exists, it will not be removed until you add a
 * different URL to the content (or you remove the preview manually.)
 */
export default function useContentWithLinkPreviewAssetCarousel({
  content,
  linksWithData,
  enabled = true,
}: ContentWithLinkPreviewProps) {
  const matchedLink = useMemo(
    () => (enabled ? validations.urlValidation.exec(content) : null),
    [content, enabled]
  )

  const [link, setLink] = useState(matchedLink !== null ? matchedLink[0] : '')
  const linkWithData = useMemo(
    () => (link ? linksWithData.find((l) => l.url === link) : undefined),
    [linksWithData, link]
  )

  const [isPersistingLink, setIsPersistingLink] = useState(linkWithData != null)
  const isFirstLinkAssetSquare = useMemo(() => {
    const linkAssets: Asset[] = linkWithData?.assetReferences ?? []
    return (
      linkAssets.length > 0 &&
      linkAssets[0].height === linkAssets[0].width &&
      linkAssets[0].format === 'ico'
    )
  }, [linkWithData?.assetReferences])

  const timeout = useRef<NodeJS.Timeout>()
  const currentMatchedLink = useRef(matchedLink)

  useEffect(() => {
    if (currentMatchedLink.current === matchedLink && !isPersistingLink) {
      return
    }

    currentMatchedLink.current = matchedLink
    clearTimeout(timeout.current)

    if (
      matchedLink !== null &&
      /**
       * If we have a valid link preview already, avoid removing it while
       * deleting the link from content. The new link parsed from content may
       * still validate as a URL, but isn't a working link, e.g.
       * "www.google.com" to "www.google.c", and we won't know if it's working
       * until /extract_preview returns.
       *
       * To solve this, check if the link has changed substantially: if the
       * new link is longer, or if it's not a subset of the existing link.
       */
      (!linkWithData ||
        matchedLink[0].length > linkWithData.url.length ||
        !linkWithData.url.startsWith(matchedLink[0]))
    ) {
      timeout.current = setTimeout(() => {
        setLink(matchedLink[0])
        setIsPersistingLink(true)
      }, 2000)
    }
  }, [matchedLink, linkWithData, isPersistingLink])

  useEffect(() => {
    return () => {
      clearTimeout(timeout.current)
    }
  }, [])

  return {
    link,
    isPersistingLink,
    setIsPersistingLink,
    linkWithData,
    isFirstLinkAssetSquare,
  }
}
